import { useCallback, useMemo, useState } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import type { FormikHelpers } from 'formik';

import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { RightDrawer } from '../../components/RightDrawer';
import { FORM_LAYOUT_STYLE, FormLayout } from '../../components/FormLayout';
import { ConfirmBeforeExitForm } from '../../components/ConfirmBeforeExitForm';
import { useActionBlocker } from '../../hooks/useActionBlocker.hook';
import { useUiContext } from '../../contexts/UiContext/hooks/useUiContext.hook';
import type { ResolvableFieldSettings } from '../../components/ResolvableFields/types/resolvable-field-settings.type';

import { MemoBeforeSubmitForm } from '../MemoBeforeSubmit';
import { memoAwareSubmitHandler } from '../MemoBeforeSubmit/utils/memo-aware-submit-handler.util';
import { useMemos } from '../MemoBeforeSubmit/hooks/useMemos.hook';
import { useResolvableServiceValueSchema } from '../ResolvableService/hooks/useResolvableServiceValueSchema.hook';
import { ResolvableServiceQuotationInfoFormStep } from '../ResolvableService/steps/QuotationInfo';
import type { SubmitHandler } from '../ResolvableServiceUser/types/submit-handler.type';
import { AlertMessages } from '../../components/AlertMessages';

import type { ManualTotalFormValues } from './types/values.type';

interface Props {
  readonly description: string;
  readonly initialValue: ManualTotalFormValues['value'];
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSubmit: SubmitHandler<ManualTotalFormValues>;
  readonly resolvableFieldSettings: ResolvableFieldSettings;
  readonly submitLabel: string;
  readonly title: string;
}

export const ManualTotalDrawerForm = ({
  description,
  initialValue,
  isOpen,
  onClose: handleClose,
  onSubmit: pHandleSubmit,
  resolvableFieldSettings,
  submitLabel,
  title,
}: Readonly<Props>) => {
  const { ui } = useUiContext();

  const resolvableServiceValueSchema = useResolvableServiceValueSchema();

  const validationSchema = useMemo(
    () => Yup.object().shape({ value: resolvableServiceValueSchema }),
    [resolvableServiceValueSchema],
  );

  const [formError, setFormError] = useState<readonly string[] | null>(null);

  const { clearMemos, memos, setMemos } = useMemos();

  const [blockableClose, setMustBlockAction] = useActionBlocker(handleClose);

  const handleSubmit = useCallback(
    (
      values: ManualTotalFormValues,
      formikHelpers: FormikHelpers<ManualTotalFormValues>,
    ) => {
      void pHandleSubmit({ values, memos, formikHelpers, setFormError })?.then(
        clearMemos,
      );
    },
    [clearMemos, pHandleSubmit, memos],
  );

  const openMemoDialog = useCallback(() => {
    setMemos((oldMemos) => ({ ...oldMemos, isOpenMemoDialog: true }));
  }, [setMemos]);

  return (
    <RightDrawer isOpen={isOpen} onDrawerClose={blockableClose} title={title}>
      <Formik
        enableReinitialize
        initialValues={{ value: initialValue }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({
          handleSubmit: formikSubmit,
          isSubmitting,
          isValid,
          submitForm,
        }) => {
          const isLoading = isSubmitting || ui.isBackgroundOperationRunning;

          return (
            <Form
              onSubmit={memoAwareSubmitHandler({
                askMemo: openMemoDialog,
                mustAskMemo: true,
                submit: formikSubmit,
              })}
              // eslint-disable-next-line react/forbid-component-props
              style={FORM_LAYOUT_STYLE}
            >
              <ConfirmBeforeExitForm setMustBlockAction={setMustBlockAction} />
              <MemoBeforeSubmitForm
                clearMemos={clearMemos}
                isLoading={isLoading}
                memos={memos}
                onConfirm={submitForm}
                setMemos={setMemos}
                submitLabel={submitLabel}
              />
              <FormLayout
                actions={
                  <Stack direction="row" spacing={2}>
                    <Button
                      disabled={!isValid}
                      loading={isLoading}
                      size="large"
                      type="submit"
                      variant="contained"
                    >
                      {submitLabel}
                    </Button>
                    <Button
                      onClick={blockableClose}
                      size="large"
                      variant="outlined"
                    >
                      Annuler
                    </Button>
                  </Stack>
                }
                error={
                  !!formError && (
                    <AlertMessages messages={formError} severity="error" />
                  )
                }
              >
                <Stack spacing={3} useFlexGap>
                  <Typography color="text.secondary">{description}</Typography>
                  <ResolvableServiceQuotationInfoFormStep
                    autoFocus
                    id="manual-total"
                    resolvableFieldSettings={resolvableFieldSettings}
                  />
                </Stack>
              </FormLayout>
            </Form>
          );
        }}
      </Formik>
    </RightDrawer>
  );
};
